import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect } from "react";
import { TemplateError } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";

import FollowUpDelayDivider from "components/FollowUpDelayDivider";
import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import FollowupSelect from "components/TemplateScreen/FollowupSelect";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import { labelSx } from "features/Influencer/Autopilot/styles";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { useRegisterSaveAsNew } from "utils/useRegisterSaveAsNew";

import StepNav from "../../StepNav";
import builderStyles from "../../styles";

interface Props {
  handleUpdateCampaign: (body: Map, skipMoveNext?: boolean) => Promise<boolean>;
  moveBack?: () => void;
  submitLoading?: boolean;
  templateError?: TemplateError | null;
  onClearTemplateError?: () => void;
  // Embedded mode: rendered inline (e.g. on the Review page) with Save/Cancel
  // instead of the step's Back/Next footer.
  embedded?: boolean;
  onSaved?: () => void;
  onCancel?: () => void;
}

export default function Templates({
  handleUpdateCampaign,
  moveBack,
  submitLoading = false,
  templateError = null,
  onClearTemplateError,
  embedded = false,
  onSaved,
  onCancel,
}: Props) {
  const { emailSettings } = useContext(OrganizationUserContext);
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);
  const {
    campaign,
    templates: selectedTemplates,
    setTemplates: setSelectedTemplates,
  } = useContext(NewCampaignContext);

  useRegisterSaveAsNew((newTemplate, editedId) =>
    setSelectedTemplates((prev) =>
      prev.map((t) => (t?.id === editedId ? newTemplate : t)),
    ),
  );

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = selectedTemplates?.slice(1);

  const handleGenerateTemplate = (contentType: TemplateContentType) => {
    setCurrentTemplateContentType(contentType);
    setCreateTemplateDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...selectedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = { ...template, delayDays: copy[index]?.delayDays };
      setSelectedTemplates(copy);
      onClearTemplateError?.();
    }
  };

  const handleDelayChange = (index: number, value: number) => {
    const copy = [...selectedTemplates];
    if (copy[index]) {
      copy[index] = { ...copy[index], delayDays: value };
      setSelectedTemplates(copy);
    }
  };

  const addFollowUp = () => {
    setSelectedTemplates([
      ...selectedTemplates,
      { ...followupTemplates?.[0], delayDays: DAYS_TO_DELAY_FOLLOWUP },
    ]);
  };

  const removeFollowUp = (index: number) => {
    const copy = [...selectedTemplates];
    copy.splice(index, 1);
    setSelectedTemplates(copy);
  };

  useEffect(() => {
    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }
    if (selectedTemplates.length > 0) return;

    const presetTemplates =
      campaign?.emailSetting?.emailSettingTemplates ||
      emailSettings?.emailSettingTemplates ||
      [];
    const matchedTemplates: Template[] = [];
    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push({
          ...template,
          delayDays:
            matchedTemplates.length === 0
              ? undefined
              : settingTemplate.delayDays ?? DAYS_TO_DELAY_FOLLOWUP,
        });
      }
    }
    if (matchedTemplates.length > 0) {
      setSelectedTemplates(matchedTemplates);
      return;
    }
    if (filteredOutreachTemplates?.length > 0) {
      setSelectedTemplates([filteredOutreachTemplates[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allTemplates?.length,
    campaign?.emailSetting?.emailSettingTemplates?.length,
    emailSettings?.emailSettingTemplates?.length,
  ]);

  const persist = (skipMoveNext: boolean) =>
    handleUpdateCampaign(
      {
        templateIds: selectedTemplates.map((t) => t.id),
        templateDelayDays: selectedTemplates.map((t) => t.delayDays ?? null),
      },
      skipMoveNext,
    );

  const handleSave = async () => {
    if (await persist(true)) onSaved?.();
  };

  const followupError =
    templateError && templateError.idx > 0
      ? { idx: templateError.idx - 1, message: templateError.message }
      : null;

  const fields = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        maxWidth: embedded ? "none" : 620,
      }}
    >
      <Box>
        <Typography sx={{ ...labelSx, mb: 1 }}>Outreach email</Typography>
        {selectedTemplates[0] ? (
          <OutreachSelect
            templates={selectedTemplates}
            handleChange={handleTemplateChange}
            filteredOutreachTemplates={filteredOutreachTemplates}
            templateFromEmailSettings={templateFromEmailSettings}
            addNewOption={() =>
              handleGenerateTemplate(TemplateContentType.OUTREACH)
            }
            templatesError={templateError}
          />
        ) : (
          <StyledLargeButton
            onClick={() => handleGenerateTemplate(TemplateContentType.OUTREACH)}
            disableElevation
          >
            Create Template
          </StyledLargeButton>
        )}
      </Box>

      <Box>
        <Typography sx={{ ...labelSx, mb: 1 }}>Follow-ups</Typography>
        {followupTemplates.length === 0 ? (
          <StyledLargeButton
            disableElevation
            disabled={outreachTemplates.length === 0}
            onClick={() => handleGenerateTemplate(TemplateContentType.FOLLOWUP)}
          >
            Create Follow-up
          </StyledLargeButton>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {selectedFollowups?.map((followup, index) => (
              <Box key={index}>
                <FollowUpDelayDivider
                  days={followup?.delayDays ?? DAYS_TO_DELAY_FOLLOWUP}
                  onChange={(val) => handleDelayChange(index + 1, val)}
                  isFirst={index === 0}
                  align="start"
                />
                <FollowupSelect
                  followup={followup}
                  index={index}
                  filteredFollowUps={filteredFollowUps}
                  templateFromEmailSettings={templateFromEmailSettings}
                  handleTemplateChange={(e: CustomEvent, idx: number) =>
                    handleTemplateChange(e, idx)
                  }
                  onRemove={() => removeFollowUp(index + 1)}
                  templatesError={followupError}
                />
              </Box>
            ))}
            {selectedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
              <Box sx={{ mt: 2 }}>
                <StyledLargeButton
                  variant="outlined"
                  disableElevation
                  onClick={addFollowUp}
                >
                  <Box
                    component="i"
                    className="fa-regular fa-add"
                    sx={{ mr: 0.5 }}
                  />{" "}
                  Follow Up
                </StyledLargeButton>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );

  if (embedded) {
    return (
      <Box>
        {fields}
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={selectedTemplates.length === 0 || submitLoading}
            sx={{ textTransform: "none" }}
          >
            {submitLoading ? <CircularProgress size={16} /> : "Save"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            disabled={submitLoading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={builderStyles.formColumn}>
      <Box sx={builderStyles.card}>
        <Typography sx={builderStyles.cardTitle}>Write your emails</Typography>
        <Typography
          sx={{ fontSize: 13, color: "text.secondary", mt: 0.5, mb: 3 }}
        >
          Start from a template or write from scratch. Use {"{Brand Name}"} and{" "}
          {"{First Name}"} to personalize.
        </Typography>
        {fields}
      </Box>

      <StepNav
        onBack={() => moveBack?.()}
        onNext={() => persist(false)}
        nextDisabled={selectedTemplates.length === 0}
        nextLoading={submitLoading}
      />
    </Box>
  );
}
